import React, { useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import store from './app/store';
import { useGetArticleItemsQuery, useGetFiltersQuery } from './data/api/api';
import { BlogList } from './components/blog-listing';
import { BlogFilters } from './components/blog-filtering';
import ReactPaginate from 'react-paginate';
import { useBlogFilters } from './hooks/use-blog-filters';
import { blogListingConfig } from './app/config';

const BlogApp: React.FC = () => {
    const appRef = useRef<HTMLDivElement>(null);
    const cfg = blogListingConfig;

    const {
        state,
        queryParams,
        submitSearch,
        setCategory,
        setPublishDate,
        setPage,
        resetFilters,
    } = useBlogFilters(cfg.pageSize);

    const { data: articlesData, error: articlesError, isLoading: articlesLoading } = useGetArticleItemsQuery(queryParams);
    const { data: filtersData, error: filtersError, isLoading: filtersLoading } = useGetFiltersQuery();

    const articles = articlesData?.results ?? [];
    const totalResults = articlesData?.totalResults ?? 0;
    const pageCount = Math.ceil(totalResults / cfg.pageSize);

    const categoryFilterValues = filtersData?.find(f => f.name === 'Categories')?.values ?? [];
    const publishDateFilterValues = filtersData?.find(f => f.name === 'PublishDate')?.values ?? [];

    const handlePageClick = (event: { selected: number }) => {
        setPage(event.selected);
        if (appRef.current) {
            // The site header is <header class="nav-container"> (sticky) — offset so it doesn't cover the list
            const nav = document.querySelector<HTMLElement>('.nav-container');
            const navHeight = nav ? nav.offsetHeight : 0;
            const appTop = appRef.current.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top: appTop - navHeight, behavior: 'smooth' });
        }
    };

    return (
        <div className="blog-listing" ref={appRef}>
            <div className="blog-listing-inner stack-lg">
                <BlogFilters
                    categoryFilterValues={categoryFilterValues}
                    publishDateFilterValues={publishDateFilterValues}
                    appliedCategory={state.category}
                    appliedPublishDate={state.publishDate}
                    searchText={state.searchText}
                    currentCount={articles.length}
                    totalCount={totalResults}
                    isLoading={filtersLoading}
                    hasError={!!filtersError}
                    onSearchSubmit={submitSearch}
                    onCategoryChange={setCategory}
                    onDateChange={setPublishDate}
                    onResetFilters={resetFilters}
                />

                <div className="stack-lg">
                    <BlogList articles={articles} isLoading={articlesLoading} hasError={!!articlesError} />
                    {pageCount > 1 ? (
                        <ReactPaginate
                            className={"blog-listing-pagination"}
                            breakLabel="..."
                            breakLinkClassName="btn-primary"
                            nextLabel={<span className="visually-hidden">Next</span>}
                            onPageChange={handlePageClick}
                            pageRangeDisplayed={5}
                            pageCount={pageCount}
                            previousLabel={<span className="visually-hidden">Previous</span>}
                            renderOnZeroPageCount={null}
                            pageLinkClassName="btn-primary"
                            activeLinkClassName="btn-secondary"
                            forcePage={state.page}
                        />
                    ) : null}
                    {!articlesLoading && totalResults === 0 ? (
                        <div className="rich-text" dangerouslySetInnerHTML={{ __html: cfg.noResultMessage }} />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export function BlogAppInit(appRoot: HTMLElement) {
    const root = ReactDOM.createRoot(appRoot);
    root.render(
        <React.StrictMode>
            <Provider store={store}>
                <BlogApp />
            </Provider>
        </React.StrictMode>
    );
}

const blogAppRoot = document.getElementById('blogApp');
if (blogAppRoot) {
    BlogAppInit(blogAppRoot);
}
